import { useCallback, useState } from 'react';
import { Form } from 'react-final-form';
import { Button, Grid, Paper, Typography } from '@mui/material';

import { citysprintApis } from '../../../apis';
import { FormInput } from '../../../components/FormInput';
import EmptySearchResults from '../../../components/EmptySearchResults';
import { FormHelpers, Validators } from '../../../helpers';
import { useCustomSnackbar } from '../../../features/CustomSnackbar';
import { useOverlay } from '../../../features/Overlay';
import { StringUtil } from '../../../utils';
import { STRINGS } from '../../../constants';
import { CITYSPRINT_HELP_FIELDS } from '../constants';

export function CitysprintHelpSupport() {
  const overlay = useOverlay();
  const { showError } = useCustomSnackbar();
  const [phoneNumber, setPhoneNumber] = useState(null);
  const [showEmptyResults, setShowEmptyResults] = useState(false);

  const resetLookupState = useCallback(() => {
    setPhoneNumber(null);
    setShowEmptyResults(false);
  }, []);

  const showBookingNotFound = useCallback(() => {
    setPhoneNumber(null);
    setShowEmptyResults(true);
    showError({ message: STRINGS.HELP_SAMEDAY_BOOKING_NOT_FOUND });
  }, [showError]);

  const handleSearch = useCallback(
    async values => {
      const bookingReference =
        values[CITYSPRINT_HELP_FIELDS.BOOKING_REFERENCE.KEY]?.trim() ?? '';
      const deliveryPostcode =
        values[CITYSPRINT_HELP_FIELDS.DELIVERY_POSTCODE.KEY]?.trim() ?? '';

      if (!bookingReference || !deliveryPostcode) {
        return;
      }

      try {
        overlay.show();
        resetLookupState();

        const response = await citysprintApis.getSupportContactNumber(
          bookingReference,
          deliveryPostcode
        );
        const { phoneNumber } = response?.data ?? response;

        if (!phoneNumber) {
          showBookingNotFound();
          return;
        }

        setPhoneNumber(phoneNumber);
      } catch {
        showBookingNotFound();
      } finally {
        overlay.hide();
      }
    },
    [overlay, resetLookupState, showBookingNotFound]
  );

  const handleFieldChange = useCallback(() => {
    resetLookupState();
  }, [resetLookupState]);

  return (
    <>
      <Form
        onSubmit={handleSearch}
        validate={values =>
          Validators.validateWithJoi(
            values,
            Validators.citysprintHelpSupportSchema
          )
        }
      >
        {({ handleSubmit, values }) => {
          const canSearch = Validators.isCitysprintHelpSearchValid(values);

          return (
            <Paper
              sx={{ p: 2, pb: 3, mb: 2 }}
              component='form'
              onSubmit={handleSubmit}
            >
              <Typography variant='h3' sx={{ mb: 2 }}>
                {STRINGS.HELP_SAMEDAY_BOOKING_REFERENCE_TITLE}
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormInput
                    name={CITYSPRINT_HELP_FIELDS.BOOKING_REFERENCE.KEY}
                    label={CITYSPRINT_HELP_FIELDS.BOOKING_REFERENCE.LABEL}
                    fullWidth
                    size='small'
                    required
                    onChange={handleFieldChange}
                    fieldProps={{
                      parse: value =>
                        String(value ?? '')
                          .trim()
                          .toUpperCase(),
                      ...FormHelpers.fieldTrimOnBlur,
                    }}
                    inputProps={{ autoComplete: 'off' }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormInput
                    name={CITYSPRINT_HELP_FIELDS.DELIVERY_POSTCODE.KEY}
                    label={CITYSPRINT_HELP_FIELDS.DELIVERY_POSTCODE.LABEL}
                    fullWidth
                    size='small'
                    required
                    onChange={handleFieldChange}
                    fieldProps={{
                      parse: StringUtil.trimFirstAndLastSpaces,
                      ...FormHelpers.fieldTrimOnBlur,
                    }}
                    inputProps={{ autoComplete: 'off' }}
                    maxLength={8}
                  />
                </Grid>
                <Grid
                  container
                  sx={{ justifyContent: 'flex-end', width: '100%' }}
                >
                  <Button
                    variant='contained'
                    type='submit'
                    size='medium'
                    disabled={!canSearch}
                    sx={{ height: 40 }}
                  >
                    {STRINGS.SEARCH}
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          );
        }}
      </Form>

      {showEmptyResults ? <EmptySearchResults /> : null}

      {phoneNumber ? (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant='h3' sx={{ mb: 2 }}>
            {STRINGS.HELP_SAMEDAY_CONTACT_TITLE}
          </Typography>
          <Typography variant='body1' sx={{ mb: 2 }}>
            {STRINGS.HELP_SAMEDAY_CONTACT_INTRO}
          </Typography>
          <Typography variant='body1'>
            {STRINGS.HELP_SAMEDAY_CONTACT_BODY_PREFIX}{' '}
            <Typography
              component='span'
              color='primary.main'
              sx={{ fontWeight: 500 }}
            >
              {phoneNumber}
            </Typography>
            {STRINGS.HELP_SAMEDAY_CONTACT_BODY_SUFFIX}
          </Typography>
        </Paper>
      ) : null}
    </>
  );
}
